import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import {
  Spinner, CalendarBlank, CheckCircle, Clock,
  Receipt, Package, Crown, Warning,
  CreditCard, Bank, CopySimple, Prohibit,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CustomerSubscriptionPage = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [subData, setSubData] = useState(null);
  const [payDialog, setPayDialog] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [payLoading, setPayLoading] = useState(false);
  const [customerSettings, setCustomerSettings] = useState(null);
  const [renewalInvoices, setRenewalInvoices] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/customer/subscription`);
      setSubData(res.data);
    } catch (err) { /* no subscription */ }
    finally { setLoading(false); }
  }, []);

  const fetchSettings = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/customer/settings`);
      setCustomerSettings(res.data);
    } catch { /* ignore */ }
  }, []);

  const fetchInvoices = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/customer/subscription-invoices`);
      setRenewalInvoices(res.data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchData(); fetchSettings(); fetchInvoices(); }, [fetchData, fetchSettings, fetchInvoices]);

  const openPayDialog = (payment) => {
    setSelectedPayment(payment);
    setPayDialog(true);
  };

  const handleBankPay = async () => {
    if (!selectedPayment) return;
    setPayLoading(true);
    try {
      await axios.post(`${API_URL}/api/customer/subscription-pay-bank`, { payment_id: selectedPayment.id });
      toast.success("Havale bildirimi gonderildi. Admin onayindan sonra islenir.");
      setPayDialog(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Islem basarisiz"); }
    finally { setPayLoading(false); }
  };

  const getCurrencySymbol = (c) => c === "USD" ? "$" : "\u20ba";

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Spinner size={32} className="animate-spin text-indigo-600" /></div>;
  }

  const sub = subData?.subscription;
  const plan = subData?.plan;
  const payments = subData?.payments || [];
  const deviceCount = subData?.device_count || 0;
  const sym = getCurrencySymbol(sub?.currency || plan?.currency);

  const isActive = sub?.status === "active";
  const isTrial = sub?.status === "trial";
  const isExpired = sub?.status === "expired";

  return (
    <div className="space-y-6" data-testid="customer-subscription-page">
      <div>
        <h1 className="text-2xl font-heading font-bold text-slate-900">{t("subscription.title")}</h1>
        <p className="text-slate-500 mt-1">Mevcut planlariniz ve odeme gecmisiniz</p>
      </div>

      {/* Current Plan */}
      {sub ? (
        <Card className={`border-2 ${isActive ? "border-emerald-200" : isTrial ? "border-blue-200" : "border-red-200"}`}>
          <CardContent className="p-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
              {/* Plan Info */}
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  {plan ? (
                    <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center">
                      <Crown size={24} weight="duotone" className="text-indigo-600" />
                    </div>
                  ) : (
                    <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center">
                      <Package size={24} weight="duotone" className="text-slate-500" />
                    </div>
                  )}
                  <div>
                    <h2 className="text-xl font-bold text-slate-900">
                      {plan?.name || sub.plan_name || "Standart"}
                    </h2>
                    <div className="flex items-center gap-2 mt-1">
                      {isActive && <Badge className="bg-emerald-100 text-emerald-700">{t("common.active")}</Badge>}
                      {isTrial && <Badge className="bg-blue-100 text-blue-700">{t("subscription.trial_period")}</Badge>}
                      {isExpired && <Badge className="bg-red-100 text-red-700">{t("customers.expired")}</Badge>}
                      <Badge className="bg-slate-100 text-slate-600">
                        {sub.billing_cycle === "monthly" ? t("common.monthly") : t("common.yearly")}
                      </Badge>
                    </div>
                  </div>
                </div>
                {plan?.description && (
                  <p className="text-sm text-slate-500">{plan.description}</p>
                )}
                {/* Features */}
                {plan?.features && plan.features.length > 0 && (
                  <ul className="space-y-1">
                    {plan.features.map((f, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-slate-600">
                        <CheckCircle size={14} className="text-emerald-500 shrink-0" />
                        {f}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Pricing & Dates */}
              <div className="lg:text-right space-y-2">
                <div>
                  <span className="text-3xl font-bold text-slate-900">
                    {sym}{sub.amount?.toLocaleString("tr-TR")}
                  </span>
                  <span className="text-slate-500 text-sm">/{sub.billing_cycle === "monthly" ? "ay" : "yil"}</span>
                </div>
                <div className="text-sm text-slate-500 space-y-1">
                  <div className="flex items-center gap-2 lg:justify-end">
                    <CalendarBlank size={14} />
                    <span>Baslangic: {new Date(sub.start_date).toLocaleDateString("tr-TR")}</span>
                  </div>
                  <div className="flex items-center gap-2 lg:justify-end">
                    <CalendarBlank size={14} />
                    <span>Bitis: {new Date(sub.end_date).toLocaleDateString("tr-TR")}</span>
                  </div>
                  {isTrial && sub.trial_end_date && (
                    <div className="flex items-center gap-2 lg:justify-end text-blue-600">
                      <Clock size={14} />
                      <span>Deneme Bitis: {new Date(sub.trial_end_date).toLocaleDateString("tr-TR")}</span>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-4 lg:justify-end text-sm">
                  <div className="bg-slate-100 px-3 py-1.5 rounded-lg">
                    <span className="text-slate-500">Cihaz: </span>
                    <span className="font-bold">{deviceCount}</span>
                    {sub.max_devices && <span className="text-slate-400">/{sub.max_devices}</span>}
                  </div>
                </div>
              </div>
            </div>

            {/* Warning if near expiry */}
            {isActive && sub.end_date && (() => {
              const daysLeft = Math.ceil((new Date(sub.end_date) - new Date()) / (1000 * 60 * 60 * 24));
              if (daysLeft <= 30 && daysLeft > 0) {
                return (
                  <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
                    <Warning size={16} className="text-amber-600 mt-0.5 shrink-0" />
                    <p className="text-sm text-amber-700">
                      Aboneliginizin bitmesine <strong>{daysLeft} gun</strong> kaldi. 
                      Suresinde yenileme yapilmazsa hesabiniz askiya alinacaktir.
                    </p>
                  </div>
                );
              }
              return null;
            })()}

            {isExpired && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                <Warning size={16} className="text-red-600 mt-0.5 shrink-0" />
                <p className="text-sm text-red-700">
                  Aboneliginiz sona ermistir. Yenileme icin lutfen yonetici ile iletisime gecin.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Package size={56} className="text-slate-300 mb-4" />
            <p className="text-lg font-medium text-slate-600">Henuz aboneliginiz yok</p>
            <p className="text-sm text-slate-400 mt-1">
              Abonelik plani atandiginda burada gorunecektir
            </p>
          </CardContent>
        </Card>
      )}

      {/* Renewal Invoices */}
      {renewalInvoices.length > 0 && (
        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-heading flex items-center gap-2">
              <Receipt size={18} className="text-amber-600" /> Yenileme Faturalari
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {renewalInvoices.map((inv) => {
                const invSym = getCurrencySymbol(inv.currency);
                const isOverdue = inv.status === "overdue";
                const isPaid = inv.status === "paid";
                return (
                  <div key={inv.id} data-testid={`renewal-invoice-${inv.id}`}
                    className={`flex items-center justify-between p-3 rounded-lg border ${
                      isPaid ? "bg-emerald-50 border-emerald-200" :
                      isOverdue ? "bg-red-50 border-red-200" :
                      "bg-amber-50 border-amber-200"
                    }`}>
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-slate-900">{inv.plan_name}</span>
                        <Badge className={
                          isPaid ? "bg-emerald-100 text-emerald-700" :
                          isOverdue ? "bg-red-100 text-red-700" :
                          "bg-amber-100 text-amber-700"
                        }>
                          {isPaid ? "Odendi" : isOverdue ? "Gecikti" : "Bekliyor"}
                        </Badge>
                      </div>
                      <p className="text-xs text-slate-500">
                        Son odeme: {new Date(inv.due_date).toLocaleDateString("tr-TR")}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-slate-900">{invSym}{(inv.amount || 0).toLocaleString("tr-TR")}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Payment History */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-heading flex items-center gap-2">
            <Receipt size={18} className="text-purple-600" /> Odeme Gecmisi
          </CardTitle>
        </CardHeader>
        <CardContent>
          {payments.length === 0 ? (
            <div className="text-center py-8">
              <Receipt size={40} className="mx-auto text-slate-300 mb-3" />
              <p className="text-sm text-slate-500">Henuz odeme kaydi yok</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>{t("common.date")}</TableHead>
                    <TableHead>{t("common.plan")}</TableHead>
                    <TableHead>Tur</TableHead>
                    <TableHead>{t("common.description")}</TableHead>
                    <TableHead>{t("common.amount")}</TableHead>
                    <TableHead>{t("common.status")}</TableHead>
                    <TableHead>{t("common.actions")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {payments.map((p) => (
                    <TableRow key={p.id} data-testid={`payment-row-${p.id}`}>
                      <TableCell className="text-sm text-slate-500">
                        {new Date(p.created_at).toLocaleDateString("tr-TR")}
                      </TableCell>
                      <TableCell>
                        {p.plan_name ? (
                          <Badge className="bg-indigo-100 text-indigo-700 text-xs">{p.plan_name}</Badge>
                        ) : "-"}
                      </TableCell>
                      <TableCell>
                        <Badge className={
                          p.type === "activation" ? "bg-emerald-100 text-emerald-700" :
                          p.type === "renewal" ? "bg-indigo-100 text-indigo-700" :
                          "bg-amber-100 text-amber-700"
                        }>
                          {p.type === "activation" ? t("billing.activation") : p.type === "renewal" ? t("billing.renewal") : t("billing.upgrade_type")}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-slate-500">{p.description}</TableCell>
                      <TableCell className="font-bold">
                        {getCurrencySymbol(p.currency)}{(p.amount || 0).toFixed(2)}
                      </TableCell>
                      <TableCell>
                        {p.status === "paid" ? (
                          <Badge className="bg-emerald-100 text-emerald-700">
                            <CheckCircle size={12} className="mr-1" /> {t("billing.paid")}
                          </Badge>
                        ) : p.status === "pending_bank" ? (
                          <Badge className="bg-blue-100 text-blue-700">
                            <Bank size={12} className="mr-1" /> {t("billing.bank_pending")}
                          </Badge>
                        ) : p.status === "rejected" ? (
                          <Badge className="bg-red-100 text-red-700">
                            <Prohibit size={12} className="mr-1" /> {t("billing.rejected")}
                          </Badge>
                        ) : (
                          <Badge className="bg-amber-100 text-amber-700">
                            <Clock size={12} className="mr-1" /> {t("billing.waiting")}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {p.status === "pending" && (
                          <Button size="sm" variant="outline" className="h-7 text-xs text-indigo-600 border-indigo-200"
                            onClick={() => openPayDialog(p)} data-testid={`pay-btn-${p.id}`}>
                            {t("subscription.pay")}
                          </Button>
                        )}
                        {p.status === "rejected" && (
                          <Button size="sm" variant="outline" className="h-7 text-xs text-amber-600 border-amber-200"
                            onClick={() => openPayDialog(p)} data-testid={`retry-pay-btn-${p.id}`}>
                            {t("subscription.retry")}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Payment Method Dialog */}
      <Dialog open={payDialog} onOpenChange={setPayDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("subscription.make_payment")}</DialogTitle>
            <DialogDescription>
              {selectedPayment?.description} - {getCurrencySymbol(selectedPayment?.currency)}{(selectedPayment?.amount || 0).toFixed(2)}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {/* Online Ode - Disabled */}
            <div className="border border-slate-200 rounded-xl p-4 opacity-60" data-testid="online-pay-option">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                    <CreditCard size={20} className="text-slate-400" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">{t("subscription.online_pay")}</p>
                    <p className="text-xs text-slate-400">Kredi/Banka karti ile odeme</p>
                  </div>
                </div>
                <Badge className="bg-slate-100 text-slate-500 text-[10px]">{t("subscription.coming_soon")}</Badge>
              </div>
            </div>

            {/* IBAN ile Ode - Active */}
            <div className="border-2 border-indigo-200 bg-indigo-50/30 rounded-xl p-4" data-testid="bank-pay-option">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                  <Bank size={20} className="text-indigo-600" />
                </div>
                <div>
                  <p className="font-medium text-slate-900">Havale / EFT ile Ode</p>
                  <p className="text-xs text-slate-500">IBAN&apos;a havale yapin, biz onaylayalim</p>
                </div>
              </div>
              {customerSettings?.bank_iban && (
                <div className="bg-white border border-indigo-100 rounded-lg p-3 space-y-2">
                  {customerSettings.company_name && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Alici:</span>
                      <span className="font-medium">{customerSettings.company_name}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-500">IBAN:</span>
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-medium text-slate-900">{customerSettings.bank_iban}</span>
                      <button onClick={() => { navigator.clipboard.writeText(customerSettings.bank_iban); toast.success(t("subscription.copied")); }}
                        className="text-indigo-600 hover:text-indigo-800" data-testid="copy-iban-btn">
                        <CopySimple size={14} />
                      </button>
                    </div>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Tutar:</span>
                    <span className="font-bold text-indigo-700">{getCurrencySymbol(selectedPayment?.currency)}{(selectedPayment?.amount || 0).toFixed(2)}</span>
                  </div>
                </div>
              )}
              <Button className="w-full mt-3 bg-indigo-600 hover:bg-indigo-700"
                onClick={handleBankPay} disabled={payLoading}
                data-testid="confirm-bank-pay-btn">
                {payLoading ? <Spinner size={14} className="animate-spin mr-1" /> : <Bank size={14} className="mr-1" />}
                Havale Yaptim, Onayla
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomerSubscriptionPage;
